import { Button, Card, Divider, Flex, Typography, theme } from 'antd'
import {
  Bot,
  History,
  Plug,
  ShieldCheck,
  ShieldQuestion,
  Ticket,
  User,
  UserPlus,
  Wrench,
  type LucideIcon
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const { Paragraph, Text, Title } = Typography

const STEPS: Array<{ key: string; Icon: LucideIcon }> = [
  { key: 'register', Icon: UserPlus },
  { key: 'scope', Icon: ShieldCheck },
  { key: 'connect', Icon: Plug },
  { key: 'authenticate', Icon: Ticket },
  { key: 'call', Icon: Wrench },
  { key: 'approve', Icon: ShieldQuestion },
  { key: 'govern', Icon: History }
]

/** Explainer block: what agents are, how the flow works, and the two types. */
export const AgentsHowItWorks = () => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [open, setOpen] = useState(true)

  const iconWrap = {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: 'var(--ant-color-fill-secondary)',
    color: token.colorPrimary,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto'
  } as const

  const typeBlock = (Icon: LucideIcon, title: string, hint: string) => (
    <Flex
      gap={10}
      align="flex-start"
      style={{ flex: '1 1 240px', minWidth: 220 }}
    >
      <span style={iconWrap}>
        <Icon size={16} />
      </span>
      <div>
        <Text strong style={{ fontSize: 13, display: 'block' }}>
          {title}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Text>
      </div>
    </Flex>
  )

  return (
    <Card styles={{ body: { padding: '14px 16px' } }}>
      <Flex justify="space-between" align="center" gap={12}>
        <Title level={5} style={{ margin: 0 }}>
          {t('workspace.agents.intro.howTitle')}
        </Title>
        <Button type="text" size="small" onClick={() => setOpen((o) => !o)}>
          {open
            ? t('workspace.agents.intro.hide')
            : t('workspace.agents.intro.show')}
        </Button>
      </Flex>

      {open ? (
        <Flex vertical gap={16} style={{ marginTop: 12 }}>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            {t('workspace.agents.intro.lead')}
          </Paragraph>

          <Flex gap={16} wrap="wrap">
            {STEPS.map(({ key, Icon }) => (
              <Flex
                key={key}
                gap={10}
                align="flex-start"
                style={{ flex: '1 1 180px', minWidth: 170 }}
              >
                <span style={iconWrap}>
                  <Icon size={16} />
                </span>
                <div>
                  <Text strong style={{ fontSize: 13, display: 'block' }}>
                    {t(`workspace.agents.intro.steps.${key}.title`)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t(`workspace.agents.intro.steps.${key}.desc`)}
                  </Text>
                </div>
              </Flex>
            ))}
          </Flex>

          <Divider style={{ margin: 0 }} />

          <div>
            <Text
              strong
              style={{ fontSize: 12, display: 'block', marginBottom: 8 }}
            >
              {t('workspace.agents.intro.typesTitle')}
            </Text>
            <Flex gap={24} wrap="wrap">
              {typeBlock(
                Bot,
                t('workspace.agents.agentType.service'),
                t('workspace.agents.agentTypeHint.service')
              )}
              {typeBlock(
                User,
                t('workspace.agents.agentType.delegated'),
                t('workspace.agents.agentTypeHint.delegated')
              )}
            </Flex>
          </div>

          <Text type="secondary" style={{ fontSize: 12 }}>
            <Text strong style={{ fontSize: 12 }}>
              {t('workspace.agents.intro.whereTitle')}:{' '}
            </Text>
            {t('workspace.agents.intro.where')}
          </Text>
        </Flex>
      ) : null}
    </Card>
  )
}
